import { useCallback, useState } from "react";
import { useGetList, useCreate, useUpdate, useDelete, useDataProvider, useNotify } from "react-admin";
import type { Comment, CommentCreate } from "@/types/comment";
import { AlertType, ResourceName } from "@/types";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";

interface UseCommentsReturn {
  comments: Comment[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
  createComment: (content: string) => Promise<void>;
  posting: boolean;
  editComment: (commentId: string, content: string) => Promise<void>;
  editingCommentId: string | null;
  replyComment: (parentId: string, content: string) => Promise<void>;
  replyingToId: string | null;
  deleteComment: (commentId: string) => Promise<void>;
  deletingCommentId: string | null;
}

/**
 * Generic comments hook for the polymorphic comments API (ADR-052 S2/S4).
 * Parameterized on the subject so one hook serves clients (now) and other
 * record types later: the resource path is `${subjectType}/${subjectId}/comments`,
 * which the dataProvider's nested-path branch routes to the per-subject endpoints.
 * created_by / partner are stamped server-side, so the create payload is just
 * { content, mentions }.
 */
export const useComments = (subjectType: ResourceName, subjectId: string | undefined): UseCommentsReturn => {
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const dataProvider = useDataProvider();
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const path = `${subjectType}/${subjectId}/comments`;

  const {
    data = [],
    isLoading,
    error,
    refetch,
  } = useGetList<Comment>(
    path,
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "created_datetime", order: "ASC" },
    },
    { enabled: !!subjectId }
  );

  const [create, { isPending: posting }] = useCreate<Comment>();
  const [update] = useUpdate();
  const [deleteOne] = useDelete();

  // returnPromise so a failed mutation REJECTS (instead of being swallowed by an
  // onError callback) — callers rely on the throw to keep the user's draft/editor
  // open on failure rather than clearing it.
  const createComment = useCallback(
    async (content: string) => {
      if (!subjectId || !content.trim()) return;

      const payload: CommentCreate = { content: content.trim(), mentions: [] };

      try {
        await create(path, { data: payload }, { returnPromise: true });
        notify("Comment added", { type: AlertType.SUCCESS });
        void refetch();
      } catch (err) {
        notifyError(err);
        throw err;
      }
    },
    [subjectId, path, create, notify, notifyError, refetch]
  );

  // Edit via PUT ${path}/${commentId} (useUpdate routes resource/id). Pessimistic
  // so the refetch reads the server's is_edited/modified_datetime.
  const editComment = useCallback(
    async (commentId: string, content: string) => {
      if (!subjectId || !content.trim()) return;
      setEditingCommentId(commentId);
      try {
        await update(
          path,
          { id: commentId, data: { content: content.trim(), mentions: [] } },
          { mutationMode: "pessimistic", returnPromise: true }
        );
        notify("Comment updated", { type: AlertType.SUCCESS });
        void refetch();
      } catch (err) {
        notifyError(err);
        throw err;
      } finally {
        setEditingCommentId(null);
      }
    },
    [subjectId, path, update, notify, notifyError, refetch]
  );

  // Reply is a POST to the ${path}/${parentId} sub-path (one-level threading),
  // which isn't the collection create — use the dataProvider's generic invoke.
  const replyComment = useCallback(
    async (parentId: string, content: string) => {
      if (!subjectId || !content.trim()) return;
      setReplyingToId(parentId);
      try {
        const payload: CommentCreate = { content: content.trim(), mentions: [] };
        await dataProvider.invoke(`${path}/${parentId}`, { data: payload });
        notify("Reply added", { type: AlertType.SUCCESS });
        void refetch();
      } catch (err) {
        notifyError(err);
        throw err;
      } finally {
        setReplyingToId(null);
      }
    },
    [subjectId, path, dataProvider, notify, notifyError, refetch]
  );

  const deleteComment = useCallback(
    async (commentId: string) => {
      if (!subjectId) return;
      setDeletingCommentId(commentId);
      try {
        await deleteOne(
          path,
          { id: commentId },
          {
            onSuccess: () => {
              notify("Comment deleted", { type: AlertType.SUCCESS });
              void refetch();
            },
            onError: (err) => {
              notifyError(err);
            },
          }
        );
      } finally {
        setDeletingCommentId(null);
      }
    },
    [subjectId, path, deleteOne, notify, notifyError, refetch]
  );

  return {
    comments: data,
    loading: isLoading,
    error: error ? String(error) : null,
    refetch,
    createComment,
    posting,
    editComment,
    editingCommentId,
    replyComment,
    replyingToId,
    deleteComment,
    deletingCommentId,
  };
};
